import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Text, Badge, Button, HStack } from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const USNV2QualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'service_date', direction: 'desc' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await callApi('/usn/v2/coding-quality', accessToken);
        if (response.ok) {
          const result = await response.json();
          setData(result.data || []);
        }
      } catch (error) {
        console.error("Error fetching V2 quality data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (accessToken) fetchData();
  }, [accessToken]);

  const sortedData = useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // 1. Handle null/undefined/empty values (push them to the bottom)
        const aIsEmpty = aValue === null || aValue === undefined || aValue === '';
        const bIsEmpty = bValue === null || bValue === undefined || bValue === '';

        if (aIsEmpty && bIsEmpty) return 0;
        if (aIsEmpty) return 1;  // a is empty, put it after b
        if (bIsEmpty) return -1; // b is empty, put it after a

        // 2. Explicitly parse numbers for score and ID columns
        const numericColumns = ['approval_score', 'needs_review_score', 'overall_preference_score', 'pid'];
        if (numericColumns.includes(sortConfig.key)) {
          // parseFloat extracts '75' from '75 / 100', but returns NaN for 'n/a'
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);
          
          const aIsNaN = isNaN(aNum);
          const bIsNaN = isNaN(bNum);

          // Push "n/a" or invalid numbers to the bottom
          if (aIsNaN && bIsNaN) return 0;
          if (aIsNaN) return 1;  
          if (bIsNaN) return -1; 
          
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // 3. Fallback for strings and dates (Procedure Name, Date of Service)
        const aString = String(aValue);
        const bString = String(bValue);
        const comparison = aString.localeCompare(bString, undefined, { numeric: true, sensitivity: 'base' });
        
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="1400px" mx="auto" p={6}>
        <Heading size="lg" mb={6} color="gray.700">USN V3 Pipeline Results</Heading>
        
        {loading ? (
          <Spinner size="xl" />
        ) : data.length === 0 ? (
          <Text>No V3 cases found.</Text>
        ) : (
          <Box bg="white" shadow="sm" borderRadius="lg" overflow="hidden">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th cursor="pointer" onClick={() => requestSort('pid')}>
                    PID{getSortIndicator('pid')}
                  </Th>
                  <Th cursor="pointer" onClick={() => requestSort('procedure_name')}>
                    Procedure Name{getSortIndicator('procedure_name')}
                  </Th>
                  <Th cursor="pointer" onClick={() => requestSort('service_date')}>
                    Date of Service{getSortIndicator('service_date')}
                  </Th>
                  <Th isNumeric cursor="pointer" onClick={() => requestSort('approval_score')}>
                    Approval Score{getSortIndicator('approval_score')}
                  </Th>
                  <Th isNumeric cursor="pointer" onClick={() => requestSort('needs_review_score')}>
                    QA Score{getSortIndicator('needs_review_score')}
                  </Th>
                  <Th isNumeric cursor="pointer" onClick={() => requestSort('overall_preference_score')}>
                    V3 Preference{getSortIndicator('overall_preference_score')}
                  </Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedData.map((row) => (
                  <Tr key={row.internal_case_id}>
                    <Td fontWeight="medium">{row.pid}</Td>
                    <Td>{row.procedure_name}</Td>
                    <Td>{row.service_date}</Td>
                    <Td isNumeric>
                      <Badge colorScheme={row.approval_score >= 70 ? 'green' : 'red'}>
                        {row.approval_score ?? 'N/A'}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme={row.needs_review_score < 30 ? 'green' : 'orange'}>
                        {row.needs_review_score ?? 'N/A'}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      {row.overall_preference_score !== null && row.overall_preference_score !== undefined ? (
                        <Badge colorScheme={row.overall_preference_score >= 60 ? 'green' : 'red'}>
                          {row.overall_preference_score} / 100
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.400">Not Run</Text>
                      )}
                    </Td>
                    <Td>
                      {row.has_legacy_run ? (
                        <Button 
                          size="sm" 
                          colorScheme="purple" 
                          onClick={() => window.open(`/usn-compare-v2/${row.external_case_id}`, '_blank')}
                        >
                          Compare V1 vs V3
                        </Button>
                      ) : (
                        <Text fontSize="sm" color="gray.400">V3 Only</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default USNV2QualityDashboard;
